import {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiClock,
  FiFileText,
  FiUser,
  FiX,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import LeaveStatusBadge from "./components/LeaveStatusBadge";

const statusLabels = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

function formatDate(dateValue) {
  if (!dateValue) return "—";

  const dateKey = dateValue.slice(0, 10);

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${dateKey}T00:00:00`));
}

export default function LeaveDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [leave, setLeave] = useState(null);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const loadLeaveRequest = useCallback(async () => {
    if (!accessToken || !id) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(`/leave/requests/${id}`, {
        accessToken,
      });

      setLeave(response);
      setComment("");
    } catch (requestError) {
      setLeave(null);
      setError(
        requestError.message || "Unable to load leave request.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadLeaveRequest();
  }, [loadLeaveRequest]);

  const handleAction = async (action) => {
    if (!leave) return;

    const actionLabel =
      action === "approve" ? "approve" : "reject";

    if (
      !window.confirm(
        `Are you sure you want to ${actionLabel} this leave request?`,
      )
    ) {
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await apiRequest(`/leave/requests/${leave.id}/${action}`, {
        method: "POST",
        accessToken,
        body: {
          comment: comment.trim() || undefined,
        },
      });

      await loadLeaveRequest();
    } catch (requestError) {
      setError(
        requestError.message ||
          `Unable to ${actionLabel} this leave request.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-80 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
      </div>
    );
  }

  if (!leave) {
    return (
      <div className="space-y-5">
        <button
          onClick={() =>
            navigate("/admindashboard/leave/requests")
          }
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <FiArrowLeft />
          Back to Leave Requests
        </button>

        <div className="rounded-3xl border border-red-200 bg-red-50 p-8">
          <h1 className="text-xl font-bold text-red-800">
            Leave request unavailable
          </h1>

          <p className="mt-2 text-red-700">
            {error || "This leave request could not be found."}
          </p>
        </div>
      </div>
    );
  }

  const primaryAssignment = leave.employee?.assignments?.[0];
  const employeeName = leave.employee?.fullName || "Unknown Employee";
  const displayStatus =
    statusLabels[leave.status] || leave.status;

  const initials = employeeName
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2);

  const approverName =
    leave.approver?.employee?.fullName ||
    leave.approver?.email ||
    null;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button
            onClick={() =>
              navigate("/admindashboard/leave")
            }
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <FiArrowLeft />
            Back to Leave Requests
          </button>

          <h1 className="mt-3 text-3xl font-bold text-gray-900">
            Leave Request Details
          </h1>

          <p className="mt-2 text-gray-500">
            Review leave information and take action.
          </p>
        </div>

        <LeaveStatusBadge status={displayStatus} />
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="h-20 w-20 rounded-3xl bg-primary-100 text-primary-800 flex items-center justify-center text-2xl font-bold">
            {initials || "EM"}
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {employeeName}
            </h2>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InfoItem
                icon={<FiUser />}
                label="Employee ID"
                value={leave.employee?.employeeCode || "—"}
              />

              <InfoItem
                icon={<FiFileText />}
                label="Designation"
                value={
                  primaryAssignment?.designation?.name ||
                  "Unassigned"
                }
              />

              <InfoItem
                icon={<FiCalendar />}
                label="Department"
                value={
                  primaryAssignment?.department?.name ||
                  "Unassigned"
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-8">
          Leave Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoCard
            title="Leave Type"
            value={leave.leaveType?.name || "—"}
            icon={<FiCalendar />}
          />

          <InfoCard
            title="Duration"
            value={`${Number(leave.totalDays)} Day(s)`}
            icon={<FiClock />}
          />

          <InfoCard
            title="From Date"
            value={formatDate(leave.startDate)}
            icon={<FiCalendar />}
          />

          <InfoCard
            title="To Date"
            value={formatDate(leave.endDate)}
            icon={<FiCalendar />}
          />
        </div>

        <div className="mt-8">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Employee Reason
          </label>

          <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100">
            <p className="text-gray-700 leading-relaxed">
              {leave.reason || "No reason provided."}
            </p>
          </div>
        </div>

        {approverName || leave.approverComment ? (
          <div className="mt-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Approval Information
            </label>

            <div className="p-5 rounded-2xl bg-primary-50 border border-primary-100">
              <p className="font-medium text-gray-900">
                {approverName
                  ? `Actioned by ${approverName}`
                  : "Action completed"}
              </p>

              <p className="mt-2 text-sm text-gray-600">
                {leave.approverComment || "No approval comment added."}
              </p>
            </div>
          </div>
        ) : null}

        <div className="mt-8">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Attachment
          </label>

          <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100 text-gray-500">
            No attachment uploaded. Document upload support will be added with secure object storage.
          </div>
        </div>
      </div>

      {leave.status === "PENDING" ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
          <h3 className="text-xl font-bold text-gray-900">
            Take Action
          </h3>

          <p className="mt-2 text-sm text-gray-500">
            An optional HR comment will be saved with this decision.
          </p>

          <textarea
            rows="4"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Optional approval or rejection comment..."
            className="mt-5 w-full rounded-2xl border border-gray-200 px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary-800"
          />

          <div className="mt-5 flex flex-col sm:flex-row gap-4">
            <button
              disabled={isSaving}
              onClick={() => handleAction("approve")}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
            >
              <FiCheck />
              {isSaving ? "Saving..." : "Approve Request"}
            </button>

            <button
              disabled={isSaving}
              onClick={() => handleAction("reject")}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
            >
              <FiX />
              {isSaving ? "Saving..." : "Reject Request"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InfoCard({ title, value, icon }) {
  return (
    <div className="p-5 rounded-2xl border border-gray-100 bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary-100 text-primary-800 flex items-center justify-center">
          {icon}
        </div>

        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="text-primary-800">{icon}</div>

      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}